import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  InputLabel,
  MenuItem,
  Modal,
  Select
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { ProfileTranslations } from "../enum/ProfileTranslations";
import {
  InputText,
  RequiredFormControl
} from "../style/ClientCreateModal.styled";
import PageToaster from "../toaster/PageToaster";

interface UserCreateModalProps {
  open: boolean;
  onClose: () => void;
  onUserCreated: () => void;
  user?: any;
}

const UserCreateModal: React.FC<UserCreateModalProps> = ({
  open,
  onClose,
  onUserCreated,
  user
}) => {
  const { isAdmin, userProfile, user_id } = useAuth();

  // estados do formulário
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userProfileId, setUserProfileId] = useState(""); // ID do perfil
  const [profiles, setProfiles] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [update, setUpdate] = useState(false);

  // avatar
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);

  // validação simples (derivada)
  const [triedSubmit, setTriedSubmit] = useState(false);
  const mustAskEmail =
    isAdmin || (user && user_id !== user?.user_id) || !update;
  const mustAskProfile = mustAskEmail;

  const invalidName = triedSubmit && !userName.trim();
  const invalidEmail = triedSubmit && mustAskEmail && !userEmail.trim();
  const invalidProfile = triedSubmit && mustAskProfile && !userProfileId.trim();

  const containerId = "toaster-user-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  // util
  const getUserInitials = (name: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1 && parts[0].length > 0)
      return parts[0][0].toUpperCase();
    if (parts.length > 1)
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return "";
  };

  const clear = () => {
    setUserName("");
    setUserEmail("");
    setUserProfileId("");
    setSelectedCompany(null);
    setFile(null);
    setAvatarUrl(null);
    setAvatarPreview(null);
    setUpdate(false);
    setTriedSubmit(false);
  };

  // busca perfis
  useEffect(() => {
    api
      .get("/profile")
      .then((r) => setProfiles(r.data))
      .catch(console.error);
  }, []);

  // busca empresas
  useEffect(() => {
    const url = isAdmin ? "/company/admin" : "/company";
    setLoadingCompanies(true);
    api
      .get(url)
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  // popula/limpa ao abrir para editar/criar
  useEffect(() => {
    if (user) {
      setUpdate(true);
      setUserName(user.user_name ?? "");
      setUserEmail(user.user_email ?? "");
      const profileIdToSet = user.user_profile_id ?? "";
      setUserProfileId(profileIdToSet);

      if (user.user_avatar) {
        const MINIO_BASE = `${process.env.REACT_APP_MINIO_API_URL}/${process.env.REACT_APP_MINIO_BUCKET}/`;
        const objectName = user.user_avatar.startsWith(MINIO_BASE)
          ? user.user_avatar.replace(MINIO_BASE, "")
          : user.user_avatar;
        api
          .post("/s3/path", { path: objectName })
          .then((r) => setAvatarUrl(r.data.url))
          .catch(() => setAvatarUrl(null));
      } else {
        setAvatarUrl(null);
      }
      setAvatarPreview(null);
      setTriedSubmit(false);
    } else {
      clear();
    }
  }, [user]);

  // preview do arquivo
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setAvatarPreview(url);
      setAvatarUrl(null);
      return () => URL.revokeObjectURL(url);
    } else {
      setAvatarPreview(null);
    }
  }, [file]);

  useEffect(() => {
    if (user && companies.length) {
      const userCompanyId =
        user.company?.company_id ?? (user as any).company_id;
      const found = companies.find((c) => c.company_id === userCompanyId);
      setSelectedCompany(found || null);
    } else if (!user) {
      setSelectedCompany(null);
    }
  }, [user, companies]);

  const handleSubmit = async () => {
    setTriedSubmit(true);

    if (invalidName || invalidEmail || invalidProfile) {
      autoError("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("user_name", userName);
      formData.append("user_email", userEmail);
      formData.append("user_profile_id", userProfileId);

      if (isAdmin && selectedCompany) {
        formData.append("company_id", String(selectedCompany.company_id));
      }
      if (file) {
        formData.append("file", file);
      }

      const route = update
        ? isAdmin
          ? `/user/admin/${user.user_id}`
          : `/user/${user.user_id}`
        : isAdmin
        ? "/user/admin"
        : "/user";

      if (update) {
        await api.put(route, formData);
        autoSuccess("Usuário atualizado com sucesso!");
      } else {
        await api.post(route, formData);
        autoSuccess("Usuário criado com sucesso!");
      }

      onUserCreated();
      clear();
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar:", err.response?.data?.message || err);
      autoError(`Erro: ${err.response?.data?.message || "Erro desconhecido"}`);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 400,
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h5
            style={{
              color: "#703",
              position: "absolute",
              top: "10px",
              right: "10px"
            }}
          >
            Campos com * são obrigatórios
          </h5>
          <h2>{update ? "Editar Usuário" : "Criar Usuário"}</h2>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Avatar
              src={avatarPreview ?? avatarUrl ?? undefined}
              sx={{ width: 80, height: 80 }}
            >
              {getUserInitials(userName || (update ? "" : "N"))}
            </Avatar>
          </Box>

          {/* Nome (obrigatório) */}
          <InputText
            fullWidth
            label="Nome"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            margin="normal"
            required
            error={invalidName}
            $invalid={invalidName}
          />

          {/* Email (condicional e obrigatório quando exibido) */}
          {mustAskEmail && (
            <InputText
              fullWidth
              label="Email"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              margin="normal"
              required
              error={invalidEmail}
              $invalid={invalidEmail}
            />
          )}

          {/* Perfil (condicional e obrigatório quando exibido) */}
          {mustAskProfile && (
            <RequiredFormControl
              fullWidth
              required
              margin="normal"
              error={invalidProfile}
              $invalid={invalidProfile}
            >
              <InputLabel id="profile-select-label">
                Selecione o Perfil
              </InputLabel>
              <Select
                labelId="profile-select-label"
                label="Selecione o Perfil"
                value={userProfileId}
                onChange={(e) => setUserProfileId(e.target.value as string)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Selecione o Perfil
                </MenuItem>
                {profiles
                  .filter((p) => {
                    if (isAdmin) return true;
                    // OWNER não pode criar outro OWNER (nem ADMIN) — só ADMIN atribui esses dois perfis.
                    if (userProfile === "OWNER")
                      return (
                        p.profile_name !== "ADMIN" && p.profile_name !== "OWNER"
                      );
                    // MANAGER não pode criar outro MANAGER (nem ADMIN/OWNER).
                    if (userProfile === "MANAGER")
                      return (
                        p.profile_name !== "ADMIN" &&
                        p.profile_name !== "OWNER" &&
                        p.profile_name !== "MANAGER"
                      );
                    return p.profile_name === "USER";
                  })
                  .map((p) => (
                    <MenuItem key={p.profile_id} value={p.profile_id}>
                      {ProfileTranslations[
                        p.profile_name as keyof typeof ProfileTranslations
                      ] || p.profile_name}
                    </MenuItem>
                  ))}
              </Select>
            </RequiredFormControl>
          )}

          {/* Empresa (opcional — admin) */}
          {isAdmin && (
            <Autocomplete
              options={companies}
              getOptionLabel={(opt) => opt.company_name || ""}
              value={selectedCompany}
              isOptionEqualToValue={(option, value) =>
                option?.company_id === value?.company_id
              }
              onChange={(_, v) => setSelectedCompany(v)}
              renderInput={(params) => (
                <InputText
                  {...params}
                  label="Empresa"
                  margin="normal"
                  // não requerido; se quiser tornar obrigatório, derive um invalidCompany e passe aqui
                />
              )}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <Button component="label" variant="outlined" fullWidth>
              Selecionar Avatar
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => e.target.files && setFile(e.target.files[0])}
              />
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button variant="contained" type="submit">
              {update ? "Atualizar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UserCreateModal;
